<template>
  <div class="dashboard-adoption-trend">
    <div class="at-header">
      <h3 class="at-title">{{ $t('org-dashboard.adoption-trend-title') }}</h3>
      <span v-if="currentRate != null" class="at-rate" :class="rateClass">
        {{ currentRate }}%
      </span>
    </div>

    <div v-if="data && data.length" class="at-chart">
      <highcharts :options="chartOptions" />
    </div>
    <div v-else class="at-empty">
      <v-icon color="#d1d5db" size="32">mdi-account-clock-outline</v-icon>
      <span>{{ $t('org-dashboard.health-no-data') }}</span>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardAdoptionTrend',
  components: { highcharts: Chart },

  props: {
    data: { type: Array, default: () => [] },
  },

  computed: {
    currentRate() {
      if (!this.data?.length) return null
      const last = this.data[this.data.length - 1]
      if (!last.totalUsers) return null
      return Math.round(last.activeUsers / last.totalUsers * 100)
    },

    rateClass() {
      if (this.currentRate >= 80) return 'at-rate--good'
      if (this.currentRate >= 50) return 'at-rate--medium'
      return 'at-rate--low'
    },

    chartOptions() {
      if (!this.data?.length) return null

      const activeData = this.data.map(d => ({
        name: d.month,
        y: d.activeUsers || 0,
      }))
      const totalData = this.data.map(d => ({
        name: d.month,
        y: d.totalUsers || 0,
      }))
      const categories = this.data.map(d => d.month)

      return {
        chart: { type: 'column', height: 200, backgroundColor: 'transparent', spacing: [8, 0, 8, 0] },
        title: { text: '' },
        credits: { enabled: false },
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'top',
          floating: true,
          itemStyle: { fontSize: '11px', fontWeight: '500', color: '#6b7280' },
        },
        xAxis: {
          categories,
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
        },
        yAxis: {
          title: { text: '' },
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineColor: '#f3f4f6',
          min: 0,
        },
        tooltip: {
          shared: true,
          backgroundColor: 'white',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          column: { borderRadius: 4, groupPadding: 0.15, pointPadding: 0.05 },
        },
        series: [
          {
            name: this.$t('org-dashboard.adoption-total-users'),
            data: totalData,
            color: '#e5e7eb',
          },
          {
            name: this.$t('org-dashboard.adoption-active-users'),
            data: activeData,
            color: '#8B5CF6',
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-adoption-trend {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.at-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.at-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.at-rate {
  font-size: 16px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 6px;

  &--good {
    background: #ecfdf5;
    color: #059669;
  }

  &--medium {
    background: #fffbeb;
    color: #d97706;
  }

  &--low {
    background: #fef2f2;
    color: #dc2626;
  }
}

.at-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;

  span {
    font-size: 12px;
    color: #9ca3af;
  }
}
</style>
